import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { fetchMyProducts } from '@/store/slices/productsSlice';
import { Product } from '@/types';
import Button from '@/components/ui/Button';
import { inventoryApi } from '@/services/api';
import toast from 'react-hot-toast';
import { getPrimaryImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

const SellerProductManagementPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, isLoading, error } = useSelector((state: RootState) => state.products);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'brand_new' | 'recycled'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'public' | 'private'>('all');

  useEffect(() => {
    // Fetch only the seller's own products
    dispatch(fetchMyProducts({ include_private: true }));
  }, [dispatch]);

  // Refresh products when component mounts (useful when navigating back from add product page)
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchMyProducts({ include_private: true }));
    }, 1000); // Small delay to ensure product creation is complete

    return () => clearTimeout(timer);
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || product.product_type === filterType;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'public' && product.is_public) ||
                         (filterStatus === 'private' && !product.is_public);
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleToggleVisibility = (productId: number, currentStatus: boolean) => {
    // TODO: Implement toggle visibility API call
    console.log('Toggle visibility for product:', productId, 'to:', !currentStatus);
  };

  const handleEditProduct = (productId: number) => {
    navigate(`/dashboard/seller/products/edit/${productId}`);
  };

  const handleDeleteProduct = (productId: number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      // TODO: Implement delete product API call
      console.log('Delete product:', productId);
    }
  };

  // Note: Inventory is now automatically created when a product is created
  // No need for manual "Add to Inventory" action anymore

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Product Management</h1>
              <p className="mt-2 text-gray-600">Manage your products and inventory</p>
            </div>
            <Button onClick={() => navigate('/dashboard/seller/products/add')}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Product Type Filter */}
            <div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Types</option>
                <option value="brand_new">Brand New</option>
                <option value="recycled">Recycled</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-center text-sm text-gray-600">
              <FunnelIcon className="h-4 w-4 mr-2" />
              {filteredProducts.length} products found
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Get started by adding your first product.'}
            </p>
            <Button onClick={() => navigate('/dashboard/seller/products/add')}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Your First Product
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Product Image */}
                <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                  <img
                    src={getPrimaryImage(product.images)}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-gray-900 line-clamp-2">{product.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      product.is_public 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {product.is_public ? 'Public' : 'Private'}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{product.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-bold text-primary-600">
                      {formatCurrency(product.price)}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      product.product_type === 'brand_new'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {product.product_type === 'brand_new' ? 'Brand New' : 'Recycled'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleVisibility(product.id, product.is_public)}
                      className="flex-1"
                    >
                      {product.is_public ? (
                        <>
                          <EyeSlashIcon className="h-4 w-4 mr-1" />
                          Hide
                        </>
                      ) : (
                        <>
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Show
                        </>
                      )}
                    </Button>
                    {/* All products now have inventory automatically - show inventory button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/dashboard/seller/inventory')}
                      title="Manage Inventory"
                      className="text-green-600 hover:text-green-700 hover:border-green-300"
                    >
                      <CubeIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditProduct(product.id)}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteProduct(product.id)}
                      className="text-red-600 hover:text-red-700 hover:border-red-300"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerProductManagementPage;
